import * as THREE from 'three'

/**
 * Ancoragem por Estacas / Piquetes de solo (bombeiros e salvamento em terreno).
 *
 * Configurações:
 *  - 1: Estaca simples (piquete metálico cravado a ~60° contra a força);
 *  - 2: Tandem 1-1 (duas estacas em linha com amarração tensa do topo da frontal à base da posterior);
 *  - 3: Sistema 1-1-1 (três estacas em linha com amarração e molinete espanhol de tensão).
 *
 * Pontos de ancoragem coincidem com `dominio/tabletop/ancoragem.ts`:
 * `base` (olhal de tração rente ao solo) e `topo` (cabeça/amarração).
 */

const COR_ACO_ESTACA = '#64748b'
const COR_CABEA_ESTACA = '#334155'
const COR_ARGOLA = '#cbd5e1'
const COR_FITA_TANDEM = '#eab308' // Amarelo de fita tubular
const COR_MOLINETE = '#b45309' // Barra do molinete (madeira/ferro)

function PiqueteUnico({ posicaoZ = 0 }: { posicaoZ?: number }) {
  // Piquete de 0.9 m cravado: 0.5 m sai do solo a ~65° contra a tração (+Z)
  return (
    <group position={[0, 0, posicaoZ]}>
      {/* Haste do piquete (inclinada ~24° para trás da vertical) */}
      <group position={[0, 0.25, -0.1]} rotation={[-0.42, 0, 0]}>
        <mesh>
          <cylinderGeometry args={[0.022, 0.022, 0.6, 12]} />
          <meshStandardMaterial color={COR_ACO_ESTACA} metalness={0.8} roughness={0.3} />
        </mesh>
      </group>

      {/* Cabeça de bater e furo superior (topo) */}
      <group position={[0, 0.5, -0.2]}>
        <mesh>
          <cylinderGeometry args={[0.035, 0.03, 0.06, 12]} />
          <meshStandardMaterial color={COR_CABEA_ESTACA} metalness={0.85} roughness={0.35} />
        </mesh>
        <mesh position={[0, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.032, 0.008, 8, 16]} />
          <meshStandardMaterial color={COR_ARGOLA} metalness={0.9} roughness={0.2} />
        </mesh>
      </group>

      {/* Argola na base rente ao solo (ponto ideal de tração para içamento) */}
      <group position={[0, 0.05, 0]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.045, 0.01, 10, 20]} />
          <meshStandardMaterial color={COR_ARGOLA} metalness={0.9} roughness={0.2} />
        </mesh>
      </group>

      {/* Área de toque para seleção */}
      <mesh position={[0, 0.25, -0.1]}>
        <cylinderGeometry args={[0.2, 0.2, 0.6, 8]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  )
}

function TiranteTandem({ de, para, comMolinete = false }: { de: [number, number, number]; para: [number, number, number]; comMolinete?: boolean }) {
  const dx = para[0] - de[0]
  const dy = para[1] - de[1]
  const dz = para[2] - de[2]
  const dist = Math.hypot(dx, dy, dz)
  const mx = (de[0] + para[0]) / 2
  const my = (de[1] + para[1]) / 2
  const mz = (de[2] + para[2]) / 2

  const pDe = new THREE.Vector3(...de)
  const pPara = new THREE.Vector3(...para)
  const direcao = pPara.clone().sub(pDe).normalize()
  const quaternio = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direcao)
  const euler = new THREE.Euler().setFromQuaternion(quaternio)

  return (
    <group>
      {/* Fita dupla de união */}
      <mesh position={[mx, my, mz]} rotation={euler}>
        <cylinderGeometry args={[0.014, 0.014, dist, 8]} />
        <meshStandardMaterial color={COR_FITA_TANDEM} roughness={0.7} />
      </mesh>

      {/* Molinete espanhol para retesar a amarração */}
      {comMolinete && (
        <group position={[mx, my, mz]}>
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.018, 0.018, 0.35, 8]} />
            <meshStandardMaterial color={COR_MOLINETE} roughness={0.8} />
          </mesh>
          <mesh>
            <sphereGeometry args={[0.028, 8, 8]} />
            <meshStandardMaterial color={COR_FITA_TANDEM} roughness={0.7} />
          </mesh>
        </group>
      )}
    </group>
  )
}

export function EstacaTabletop({ configuracao = 0 }: { configuracao?: number }) {
  const cfg = Math.max(0, Math.min(2, Math.round(configuracao)))

  return (
    <group name="estaca-ancoragem">
      {/* Estaca frontal primária */}
      <PiqueteUnico posicaoZ={0} />

      {/* Segunda estaca (Tandem 1-1 ou Linha 1-1-1) */}
      {cfg >= 1 && (
        <>
          <PiqueteUnico posicaoZ={-1.0} />
          <TiranteTandem
            de={[0, 0.5, -0.2]} // Topo da estaca 1
            para={[0, 0.05, -1.0]} // Base da estaca 2
            comMolinete={cfg === 2}
          />
        </>
      )}

      {/* Terceira estaca (Linha 1-1-1) */}
      {cfg >= 2 && (
        <>
          <PiqueteUnico posicaoZ={-2.0} />
          <TiranteTandem
            de={[0, 0.5, -1.2]} // Topo da estaca 2
            para={[0, 0.05, -2.0]} // Base da estaca 3
            comMolinete={true}
          />
        </>
      )}
    </group>
  )
}
